import { apiSlice } from '../api/apiSlice'

export type FeedWeights = {
  followedCreator: number
  creatorAffinity: number
  category: number
  tag: number
  trending: number
  freshness: number
  spending: number
}
export type CreatorVideoBody = {
  youtubeId: string
  title: string
  productIds: string[]
  isActive: boolean
  displayOrder: number
}
export type LinkedProduct = { _id: string; name: string }
export type CreatorVideo = CreatorVideoBody & {
  _id: string
  thumbnailUrl: string
  embedUrl: string
  watchUrl: string
  linkedProducts: LinkedProduct[]
}
export type ImpressionReport = {
  from: string
  to: string
  totals: { impressions: number; viewers: number }
  daily: { day: string; impressions: number }[]
  cards: { type: string; id: string; impressions: number; viewers: number }[]
}
type Envelope<T> = { success: boolean; data: T }

export const discoverApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFeedWeights: builder.query<
      { weights: FeedWeights; defaults: FeedWeights },
      void
    >({
      query: () => 'v2/discover/weights',
      transformResponse: (
        response: Envelope<{ weights: FeedWeights; defaults: FeedWeights }>,
      ) => response.data,
      providesTags: ['DISCOVER_WEIGHTS'],
    }),
    saveFeedWeights: builder.mutation<Envelope<FeedWeights>, FeedWeights>({
      query: (body) => ({ url: 'v2/discover/weights', method: 'PUT', body }),
      invalidatesTags: ['DISCOVER_WEIGHTS'],
    }),
    getDiscoverImpressions: builder.query<
      ImpressionReport,
      { from: string; to: string }
    >({
      query: (params) => ({ url: 'v2/discover/impressions', params }),
      transformResponse: (response: Envelope<ImpressionReport>) =>
        response.data,
    }),
    getCreatorVideos: builder.query<CreatorVideo[], string>({
      query: (creatorId) => `v2/creators/${creatorId}/videos`,
      transformResponse: (response: Envelope<CreatorVideo[]>) => response.data,
      providesTags: (_result, _error, creatorId) => [
        { type: 'CREATOR_VIDEO', id: creatorId },
      ],
    }),
    saveCreatorVideo: builder.mutation<
      Envelope<CreatorVideo>,
      { creatorId: string; videoId?: string; body: CreatorVideoBody }
    >({
      query: ({ creatorId, videoId, body }) => ({
        url: `v2/creators/${creatorId}/videos${
          videoId === undefined ? '' : `/${videoId}`
        }`,
        method: videoId === undefined ? 'POST' : 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { creatorId }) => [
        { type: 'CREATOR_VIDEO', id: creatorId },
      ],
    }),
    deleteCreatorVideo: builder.mutation<
      Envelope<unknown>,
      { creatorId: string; videoId: string }
    >({
      query: ({ creatorId, videoId }) => ({
        url: `v2/creators/${creatorId}/videos/${videoId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { creatorId }) => [
        { type: 'CREATOR_VIDEO', id: creatorId },
      ],
    }),
  }),
})
export const {
  useGetFeedWeightsQuery,
  useSaveFeedWeightsMutation,
  useGetDiscoverImpressionsQuery,
  useGetCreatorVideosQuery,
  useSaveCreatorVideoMutation,
  useDeleteCreatorVideoMutation,
} = discoverApi
